import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

import { DownArrowIcon, UpArrowIcon } from "../../assets";

interface LinkItem {
  to: string;
  label: string;
  roles?: string[];
}

/** A titled, collapsible set of links inside a section, e.g. System Configuration → Helpdesk. */
interface LinkGroup {
  id: string;
  label: string;
  children: LinkItem[];
}

type MenuLink = LinkItem | LinkGroup;

const isLinkGroup = (link: MenuLink): link is LinkGroup => "children" in link;

interface DropdownMenuProps {
  title: string;
  icon?: React.ReactNode;
  links: MenuLink[];
  isActiveLink: (path: string) => boolean;
  isOpen: boolean;
  onDropdownClick: () => void;
  userIconClass: boolean;
  userRole?: string;
  handleLinkNavigation: (
    path: string,
    e: React.MouseEvent<HTMLElement>
  ) => void;
}

interface LinkGroupItemProps {
  group: LinkGroup;
  isActiveLink: (path: string) => boolean;
  renderLink: (link: LinkItem) => React.ReactNode;
}

/**
 * A group's title with its links nested beneath. Starts open when one of its
 * pages is the current one, and opens again whenever you land on one.
 */
const LinkGroupItem: React.FC<LinkGroupItemProps> = ({ group, isActiveLink, renderLink }) => {
  const hasActiveLink = group.children.some((link) => isActiveLink(link.to));
  const [isGroupOpen, setIsGroupOpen] = useState(hasActiveLink);
  const listId = `layoutSidemenu-group-${group.id}`;

  useEffect(() => {
    if (hasActiveLink) setIsGroupOpen(true);
  }, [hasActiveLink]);

  return (
    <li className="layoutSidemenu__list--item layoutSidemenu__group">
      <button
        type="button"
        className="layoutSidemenu__list--link layoutSidemenu__groupToggle"
        aria-expanded={isGroupOpen}
        aria-controls={listId}
        onClick={() => setIsGroupOpen((open) => !open)}
      >
        <span>{group.label}</span>
        <span className="arrowDown">
          {isGroupOpen ? <UpArrowIcon /> : <DownArrowIcon />}
        </span>
      </button>
      <ul id={listId} className={`layoutSidemenu__nested ${isGroupOpen ? "open" : ""}`}>
        {group.children.map(renderLink)}
      </ul>
    </li>
  );
};

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  title,
  icon,
  links,
  isActiveLink,
  isOpen,
  onDropdownClick,
  userIconClass,
  userRole,
  handleLinkNavigation,
}) => {
  /**
   * Determines if a link should be shown based on the user's role.
   *
   * @param link - The link item to check
   * @returns {boolean} - Returns true if the link should be shown, otherwise false
   */
  const shouldShowLink = (link: LinkItem): boolean => {
    if (link.roles === undefined || link.roles.length === 0) {
      return false; // Show link if no roles are specified
    }

    if (link.roles.includes("*")) {
      return true; // Show link if roles include "*"
    }

    if (userRole && link.roles.includes(userRole)) {
      return true; // Show link if user's role is included
    }

    return false; // Hide link if none of the conditions are met
  };

  /**
   * Filter the links array to only include links that should be shown to the user.
   * A group keeps only its visible links, and is left out when none are.
   */
  const filteredLinks = links.reduce<MenuLink[]>((shown, link) => {
    if (!isLinkGroup(link)) return shouldShowLink(link) ? [...shown, link] : shown;
    const children = link.children.filter(shouldShowLink);
    return children.length ? [...shown, { ...link, children }] : shown;
  }, []);
  const rowCount = filteredLinks.reduce(
    (count, link) => count + (isLinkGroup(link) ? link.children.length + 1 : 1),
    0
  );
  const [isSublistOpen, setIsSublistOpen] = useState(false);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [submenuStyle, setSubmenuStyle] = useState<React.CSSProperties>({});
  const liRef = useRef<HTMLLIElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSubmenu = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (!liRef.current || !document.body.classList.contains("sidebar-hidden-desktop")) return;
    const liRect = liRef.current.getBoundingClientRect();
    const estimatedHeight = (rowCount + 1) * 44;
    const viewportHeight = window.innerHeight;
    const top = liRect.top + estimatedHeight > viewportHeight
      ? Math.max(0, viewportHeight - estimatedHeight)
      : liRect.top;
    setSubmenuStyle({ top: `${top}px` });
    setIsHoverOpen(true);
  };

  const hideSubmenu = () => {
    hideTimeoutRef.current = setTimeout(() => setIsHoverOpen(false), 100);
  };

  const renderLink = (link: LinkItem) => (
    <li
      key={link.to}
      className={`layoutSidemenu__list--item ${isActiveLink(link.to) ? "layoutSidemenu__selected" : ""
        }`}
    >
      <Link
        className="layoutSidemenu__list--link"
        onClick={(e) => {
          setIsHoverOpen(false);
          handleLinkNavigation(link.to, e);
        }}
        to={link.to}
      >
        <span>{link.label}</span>
      </Link>
    </li>
  );

  return (
    <li
      ref={liRef}
      onMouseEnter={showSubmenu}
      onMouseLeave={hideSubmenu}
      className={`layoutSidemenu__list--item ${isOpen ? "layoutSidemenu__selected" : ""
        }`}
    >
      <div className="layoutSidemenu__list--link" onClick={onDropdownClick}>
        {icon && (
          <span className={`icon ${userIconClass && "icon--user"}`}>
            {icon}
          </span>
        )}
        <span>{title}</span>
        <span className="arrowDown">
          {isOpen ? <UpArrowIcon /> : <DownArrowIcon />}
        </span>
      </div>
      <ul
        className={`layoutSidemenu__list layoutSidemenu__list--submenu border_0 ${isOpen ? "open" : ""} ${isHoverOpen ? "hover-open" : ""}`}
        style={isHoverOpen ? submenuStyle : {}}
        onMouseEnter={() => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }}
        onMouseLeave={hideSubmenu}
        onClick={() => setIsSublistOpen(true)}
      >
        <li className="layoutSidemenu__list--item layoutSidemenu__list--itemTitle">
          <div className="layoutSidemenu__list--link" style={{ cursor: "default", pointerEvents: "none" }}>
            <span>{title}</span>
          </div>
        </li>
        {filteredLinks?.map((link) =>
          isLinkGroup(link) ? (
            <LinkGroupItem
              key={link.id}
              group={link}
              isActiveLink={isActiveLink}
              renderLink={renderLink}
            />
          ) : (
            renderLink(link)
          )
        )}
      </ul>
    </li>
  );
};

export default DropdownMenu;
